/**
 * Utilitaires de date et échelle de temps du Gantt.
 *
 * Fonctions pures uniquement : aucun accès DOM, aucun `new Date()` implicite côté rendu.
 * Toutes les dates manipulées sont des chaînes ISO `YYYY-MM-DD`, interprétées en UTC pour
 * éviter les décalages de fuseau horaire lors des additions de jours.
 */

const DAY_MS = 86_400_000;

/** Parse une date ISO `YYYY-MM-DD` en `Date` UTC (minuit). */
export function parseISO(dateStr) {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

/** Formate une `Date` UTC en chaîne ISO `YYYY-MM-DD`. */
export function formatISO(date) {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Ajoute (ou retranche si négatif) `days` jours à une date ISO. */
export function addDays(dateStr, days) {
  const date = parseISO(dateStr);
  date.setUTCDate(date.getUTCDate() + days);
  return formatISO(date);
}

/** Nombre de jours entre deux dates ISO (`toStr - fromStr`), peut être négatif. */
export function diffDays(fromStr, toStr) {
  return Math.round((parseISO(toStr).getTime() - parseISO(fromStr).getTime()) / DAY_MS);
}

/** Vrai si la date ISO tombe un samedi ou un dimanche. */
export function isWeekend(dateStr) {
  const day = parseISO(dateStr).getUTCDay();
  return day === 0 || day === 6;
}

/** Zooms disponibles, du plus fin au plus large. */
export const ZOOM_LEVELS = Object.freeze(['day', 'week', 'month', 'quarter']);

/** Largeur en pixels d'un jour, par niveau de zoom. */
export const ZOOM_PX_PER_DAY = Object.freeze({
  day: 32,
  week: 10,
  month: 4,
  quarter: 1.4,
});

/**
 * Construit une échelle de temps : conversion date ISO <-> position x en pixels.
 * `rangeStart`/`rangeEnd` bornent la grille rendue (colonne de gauche exclue).
 * `availableWidth` (optionnel, px) est la largeur mesurée de la zone de temps : la largeur d'un
 * jour vaut alors `max(valeur du zoom, largeur disponible / nombre de jours)`. La valeur du
 * zoom reste un minimum (au-delà, la grille déborde et défile). Fonction pure : la mesure du
 * DOM est faite par l'appelant.
 */
export function createScale({ zoom, rangeStart, rangeEnd, availableWidth }) {
  if (!ZOOM_LEVELS.includes(zoom)) {
    throw new Error(`Zoom inconnu : ${zoom}`);
  }
  const totalDays = Math.max(diffDays(rangeStart, rangeEnd), 1);
  // `floor` (au millième de pixel) : évite qu'un arrondi flottant fasse dépasser la grille d'une fraction de pixel
  // et déclenche une barre de défilement horizontale inutile.
  const fitPxPerDay =
    Number.isFinite(availableWidth) && availableWidth > 0
      ? Math.floor((Math.floor(availableWidth) / totalDays) * 1000) / 1000
      : 0;
  const pxPerDay = Math.max(ZOOM_PX_PER_DAY[zoom], fitPxPerDay);

  /** Position x (px) du début du jour `dateStr`, relative à `rangeStart`. */
  function x(dateStr) {
    return diffDays(rangeStart, dateStr) * pxPerDay;
  }

  /** Date ISO du jour le plus proche de la position x (px) donnée. */
  function date(px) {
    return addDays(rangeStart, Math.round(px / pxPerDay));
  }

  return Object.freeze({
    zoom,
    pxPerDay,
    rangeStart,
    rangeEnd,
    totalWidth: totalDays * pxPerDay,
    x,
    date,
  });
}
